import { useCallback, useMemo, useState } from 'react';

import type { FormResponses, FormResult, Invitation } from '@sine/sine-core-api-types';
import type { JoiForm } from '@sine/sine-ui-components';
import { useNotification } from '@sine/sine-ui-components';

import type { PassWithResponseSummaries } from '../../../activity/helpers/activityFeedCache.helpers';
import formService from '../../../services/form/form.service';
import { storage } from '../../../services/storage/storage.service';
import { useSignIn } from '../SigninProvider';
import type { PrePass, VisitorDetailsFormValues } from '../components/visitorCheckInModal.types';
import { applyFormResponseToPass } from '../helpers/applyFormValues';
import { type PrefillPass } from '../helpers/prefillPass';
import { useProcessIdentityCheck } from './useProcessIdentityCheck';
import { useSignInVisitor } from './useSignIn';

const PAGE = {
  VISITOR_DETAILS: 'VISITOR_DETAILS',
  CHECK_IN_FORM: 'CHECK_IN_FORM',
};

interface UseCheckInWorkflowParams {
  invitation?: Invitation;
  pass?: PassWithResponseSummaries;
  prefillPass: PrefillPass;
  onClose: () => void;
  visitorDetailsFormRef: React.MutableRefObject<JoiForm<VisitorDetailsFormValues> | undefined>;
  checkInFormRef: React.MutableRefObject<JoiForm<any> | undefined>;
  firstNameInput: React.MutableRefObject<HTMLInputElement | null>;
}

export function useCheckInWorkflow({
  invitation,
  pass,
  prefillPass,
  onClose,
  visitorDetailsFormRef,
  checkInFormRef,
  firstNameInput,
}: UseCheckInWorkflowParams) {
  const { addNotification } = useNotification();
  const { siteRequirements, checkInAnother, selectedVisitorType } = useSignIn();

  const [page, setPage] = useState(PAGE.VISITOR_DETAILS);
  const [prefilledForm, setPrefilledForm] = useState<FormResponses | FormResult | null>(null);
  const [prePass, setPrePass] = useState<PrePass | undefined>();
  const [formResetKey, setFormResetKey] = useState(0);
  const [isProcessingCheckIn, setIsProcessingCheckIn] = useState(false);

  const hasCheckInForm = !!siteRequirements?.form;
  const isPrefilled = !!(invitation || pass);

  const formRequired = useMemo(() => {
    if (!selectedVisitorType?.value) return true;

    return formService.checkFormVisitorTypes(siteRequirements?.form?.fields, selectedVisitorType.value as string);
  }, [siteRequirements, selectedVisitorType?.value]);

  const { signIn, shouldCloseModal, isLoadingSignIn } = useSignInVisitor();

  const { processIdentityCheck, isLoadingIdentityCheck } = useProcessIdentityCheck({
    hasCheckInForm,
    isPrefilled,
    prefillPass,
    formRequired: formRequired ?? false,
    onPageChange: setPage,
    onPrefilledFormChange: setPrefilledForm,
    onPrePassChange: setPrePass,
    PAGE,
  });

  const createPass = useCallback(
    async (newPass: PrePass) => {
      const createdPass = await signIn(newPass);

      if (checkInAnother && !pass && !invitation) {
        setPage(PAGE.VISITOR_DETAILS);
        setPrefilledForm(null);
        setPrePass(undefined);
        checkInFormRef.current = undefined;
        visitorDetailsFormRef.current = undefined;
        setFormResetKey((prev) => prev + 1);

        firstNameInput.current?.focus();
      } else {
        shouldCloseModal && onClose();
      }

      return createdPass;
    },
    [
      signIn,
      checkInAnother,
      pass,
      invitation,
      checkInFormRef,
      visitorDetailsFormRef,
      firstNameInput,
      shouldCloseModal,
      onClose,
    ],
  );

  const applyFormResponses = useCallback(
    async (newPass: PrePass) => {
      if (!formRequired) return;

      try {
        if (checkInFormRef.current?.validate()) {
          newPass = applyFormResponseToPass(siteRequirements!.form!, checkInFormRef.current.values, newPass);

          await createPass(newPass);
        } else {
          addNotification({
            heading: 'Your form response has been rejected',
            message: 'Please review your responses',
            variant: 'danger',
          });
        }
      } catch (error) {
        addNotification({
          heading: 'Your form response has been rejected',
          message: 'Please review your responses',
          variant: 'danger',
        });
      }
    },
    [formRequired, checkInFormRef, siteRequirements, createPass, addNotification],
  );

  const onNext = useCallback(async () => {
    if (isProcessingCheckIn) return;

    storage.setItem('checkinAnother', String(checkInAnother));

    if (visitorDetailsFormRef.current?.validate()) {
      setIsProcessingCheckIn(true);
      addNotification({
        message: 'Processing your check in',
        variant: 'info',
      });

      try {
        const visitorDetails = visitorDetailsFormRef.current.validateAndTransform().values;
        await processIdentityCheck(visitorDetails);
      } catch (error: any) {
        if (error.code === 'CHECK_IN_NOT_PERMITTED') {
          onClose();
        }
      } finally {
        setIsProcessingCheckIn(false);
      }
    }
  }, [checkInAnother, visitorDetailsFormRef, addNotification, processIdentityCheck, onClose, isProcessingCheckIn]);

  const onCheckIn = useCallback(async () => {
    if (isProcessingCheckIn) return;

    storage.setItem('checkinAnother', String(checkInAnother));
    try {
      setIsProcessingCheckIn(true);

      if (!hasCheckInForm || !formRequired) {
        if (visitorDetailsFormRef.current?.validate()) {
          addNotification({
            message: 'Processing your check in',
            variant: 'info',
          });

          try {
            const visitorDetails = visitorDetailsFormRef.current.validateAndTransform().values;
            const _prePass = await processIdentityCheck(visitorDetails);

            if (_prePass) {
              await createPass(_prePass);
            }
          } catch (error: any) {
            if (error.code === 'CHECK_IN_NOT_PERMITTED') {
              onClose();
            }
          }
        }
      } else {
        await applyFormResponses(prePass!);
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsProcessingCheckIn(false);
    }
  }, [
    checkInAnother,
    hasCheckInForm,
    formRequired,
    visitorDetailsFormRef,
    addNotification,
    processIdentityCheck,
    createPass,
    onClose,
    applyFormResponses,
    prePass,
    isProcessingCheckIn,
  ]);

  const onBack = useCallback(() => {
    setPage(PAGE.VISITOR_DETAILS);
    visitorDetailsFormRef.current?.onChangeAll(visitorDetailsFormRef.current.values);
  }, [visitorDetailsFormRef]);

  return {
    page,
    prefilledForm,
    hasCheckInForm,
    formRequired,
    formResetKey,
    isSubmitting: isProcessingCheckIn || isLoadingSignIn || isLoadingIdentityCheck,
    onNext,
    onCheckIn,
    onBack,
    PAGE,
  };
}
